import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Card, Radio, Space, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import { BgColorsOutlined, CodepenOutlined, EditOutlined, PictureOutlined } from '@ant-design/icons';
import { ingestionStore } from '../store/IngestionStore';
import { ingestionApi } from '../ingestionApi';
import { geometryGroupKey, geometryGroupLabel, sortedVariants, variationLabel } from '../ingestionHelpers';
import { VariantAssetViewer, activeDiffuseOf } from './VariantAssetViewer';
import { TextureAssetPanel } from './TextureAssetPanel';
import { ModelEditor, latestOriginalGlb } from './ModelEditor';
import { MODEL_ENGINES, isBedType } from '../itemTypes';
import { ModelSection, sectionTitleOf } from './ModelSection';
import { VariantImages } from './ProductImagesTab';

const { Text } = Typography;

const MODEL_TYPES = ['FloorItem', 'WallItem', 'Door', 'Crib', 'ToddlerBed', 'TwinBed', 'QueenBed', 'Bassinet'];
const engineOf = (item) => (item.modelEngine === 'opus' ? 'opus' : 'meshy');
const TEXTURE_TYPES = ['Texture', 'WallPrint', 'Pillow', 'Curtain'];

export const assetSpec = (itemType, rugClass) => {
    // A rug's asset kind follows its class: complex → 3D model, flat (default) → texture.
    if (itemType === 'Rug' && rugClass === 'complex') {
        return { required: '3D model', accept: '.glb,.babylon', needed: true, model: true };
    }
    if (itemType === 'Rug') {
        return { required: 'texture image', accept: 'image/*', needed: true, texture: true };
    }
    if (MODEL_TYPES.includes(itemType)) {
        return { required: '3D model', accept: '.glb,.babylon', needed: true, model: true };
    }
    if (TEXTURE_TYPES.includes(itemType)) return { required: 'texture image', accept: 'image/*', needed: true, texture: true };
    return { required: 'none', accept: '', needed: false };
};

/** The original the compression step consumes (mirrors the server's selection): the newest model, or the active diffuse texture. */
export const compressionSourceOf = (spec, assets) => {
    if (spec.model) {
        return (
            [...(assets || [])]
                .filter((a) => a.kind === 'original' && (a.assetFormat === 'glb' || a.assetFormat === 'babylon'))
                .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0] || null
        );
    }
    return spec.texture ? activeDiffuseOf(assets) : null;
};

export const Step4Assets = observer(({ item, refreshTick }) => {
    const [byVariant, setByVariant] = useState({});
    const [modelEditorOpen, setModelEditorOpen] = useState(false);
    const spec = assetSpec(item.itemType, item.rugClass);
    const isBed = isBedType(item.itemType);
    const engine = engineOf(item);
    const opus = spec.model && engine === 'opus';
    const labelOf = (variant) => variationLabel(item, variant);

    const loadAssets = async () => {
        try {
            const rows = await ingestionApi.getAssets(item.id);
            setByVariant(Object.fromEntries(rows.map((r) => [r.variantId, r])));
        } catch (e) {
            message.error(e.message || 'Failed to load assets');
        }
    };

    useEffect(() => {
        loadAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id, refreshTick]);

    // Generation runs for minutes — poll while the step is running.
    useEffect(() => {
        if (item.status !== 'running') return undefined;
        const interval = setInterval(() => {
            ingestionStore.refreshCurrentItem(item.id);
            loadAssets();
        }, 4000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.status, item.id]);

    const handleUpload = async (variantId, file) => {
        try {
            await ingestionApi.uploadAsset(item.id, variantId, file);
            message.success(`Uploaded ${file.name}`);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Upload failed');
        }
        return false; // prevent antd's default upload
    };

    const handleUseAsAsset = async (variantId, source) => {
        try {
            const { applied } = await ingestionApi.useAsAsset(item.id, variantId, source);
            if (applied === 0) {
                message.info('This image is already the asset');
            } else {
                message.success(applied > 1 ? `Image set as the asset for ${applied} variants` : 'Image set as the asset');
            }
            const rows = await ingestionApi.getAssets(item.id);
            setByVariant(Object.fromEntries(rows.map((r) => [r.variantId, r])));
            // Every variant has its asset now — run the step so the item advances.
            if ((item.variants || []).every((v) => rows.find((r) => r.variantId === v.id)?.hasRequired)) {
                await ingestionStore.runStep(item.id, 4);
                await ingestionStore.refreshCurrentItem(item.id);
            }
        } catch (e) {
            message.error(e.message || 'Failed to set as asset');
        }
    };

    const handleGenerateTextureFrom = async (variantId, source) => {
        try {
            await ingestionApi.setTextureSource(item.id, variantId, source);
            message.success('Generating the texture from this image — this takes a minute');
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to generate the texture');
        }
    };

    const handleDownload = async (asset, filename) => {
        try {
            const blob = await ingestionApi.getAssetBlob(item.id, asset.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(e.message || 'Download failed');
        }
    };

    const [regenerating, setRegenerating] = useState(null);
    const [retexturing, setRetexturing] = useState(null);
    const [texturingRest, setTexturingRest] = useState(null);
    const [configByVariant, setConfigByVariant] = useState({});

    // A generation runs for minutes in the worker; keep the clicked variant's
    // button locked until the item leaves `running` (the poll above flips it
    // back), not just until the enqueue call returns.
    const itemRunning = item.status === 'running';
    useEffect(() => {
        if (!itemRunning) {
            setRegenerating(null);
            setRetexturing(null);
            setTexturingRest(null);
        }
    }, [itemRunning]);

    const handleRegenerate = async (variantId, feedback) => {
        setRegenerating(variantId);
        try {
            const body = opus ? (feedback ? { feedback } : undefined) : spec.model ? configByVariant[variantId] : undefined;
            await ingestionApi.regenerateVariantAsset(item.id, variantId, body);
            message.success(opus ? 'Building with Opus — this takes a few minutes' : 'Regenerating — this takes a few minutes');
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to regenerate');
            setRegenerating(null);
        }
    };

    const handleRetexture = async (variantId, config) => {
        setRetexturing(variantId);
        try {
            await ingestionApi.retextureVariantAsset(item.id, variantId, config);
            message.success('Retexturing — this takes a few minutes');
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to retexture');
            setRetexturing(null);
        }
    };

    const hasModel = (v) => !!byVariant[v.id]?.hasRequired;
    const allReady = (item.variants || []).every((v) => !spec.needed || byVariant[v.id]?.hasRequired);
    const editableModelCount = spec.model ? (item.variants || []).filter((v) => latestOriginalGlb(byVariant[v.id])).length : 0;

    // Variants differing only by color/texture share geometry — one model per
    // group is generated; the rest get a cheap retexture from it.
    const groups = {};
    sortedVariants(item).forEach((v) => {
        const key = geometryGroupKey(item, v);
        (groups[key] = groups[key] || []).push(v);
    });
    const groupList = Object.values(groups);
    const representativeOf = (group) =>
        group
            .filter(hasModel)
            .sort((a, b) => new Date(latestOriginalGlb(byVariant[b.id])?.createdAt || 0) - new Date(latestOriginalGlb(byVariant[a.id])?.createdAt || 0))[0]
        || group.find((v) => v.imageId || (v.meshyConfig?.model?.imageUrls || []).some(Boolean))
        || group[0];
    const representatives = new Set(groupList.map((g) => representativeOf(g).id));
    const repsReady = spec.model && groupList.length > 0 && groupList.every((g) => g.some(hasModel));
    const textureRemainingState = spec.model && !allReady && repsReady;

    const handleTextureRest = async (rep) => {
        setTexturingRest(rep.id);
        try {
            const { queued } = await ingestionApi.textureRemaining(item.id, rep.id);
            message.success(`Texturing ${queued} variant(s) from this model — the item continues automatically when every variant has one`);
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to texture the rest');
            setTexturingRest(null);
        }
    };

    const handleRunStep = async () => {
        try {
            if (textureRemainingState) {
                const { queued } = await ingestionApi.textureRemaining(item.id);
                message.success(`Texturing ${queued} variant(s) — the item continues automatically when done`);
            } else {
                await ingestionStore.runStep(item.id, 4);
                message.success('Assets step queued');
            }
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to run assets step');
        }
    };

    const [settingEngine, setSettingEngine] = useState(false);
    const handleEngine = async (modelEngine) => {
        setSettingEngine(true);
        try {
            await ingestionApi.setModelEngine(item.id, modelEngine);
            message.success(`Models by ${MODEL_ENGINES.find((e) => e.value === modelEngine)?.label || modelEngine} from the next generation`);
            await ingestionStore.refreshCurrentItem(item.id);
        } catch (e) {
            message.error(e.message || 'Failed to set the model engine');
        } finally {
            setSettingEngine(false);
        }
    };
    const [settingRugClass, setSettingRugClass] = useState(false);
    const handleRugClass = async (rugClass) => {
        setSettingRugClass(true);
        try {
            await ingestionApi.setRugClass(item.id, rugClass);
            message.success(`Rug set to ${rugClass === 'complex' ? 'complex 3D shape' : 'flat plane'}`);
            await ingestionStore.refreshCurrentItem(item.id);
            await loadAssets();
        } catch (e) {
            message.error(e.message || 'Failed to set rug class');
        } finally {
            setSettingRugClass(false);
        }
    };

    const busyFor = (variantId) => regenerating === variantId || retexturing === variantId || texturingRest === variantId || !!ingestionStore.runningStep;

    const headerText = spec.model
        ? textureRemainingState
            ? 'Every model is built. "Texture rest" on a model textures the variants that share it, styled by each one\'s own image; the item continues automatically once every variant has a model.'
            : !opus && isBed
              ? 'Beds are modeled as bare frames: the image model first removes the mattress and bedding from the photo, then meshy.ai builds the frame from that (one variant per shape; the rest are retextured from it). Add the mattress + blanket in the model editor, then run the step again to continue.'
              : null
        : spec.texture
          ? null
          : 'This item type needs no asset files. Run the step to advance.';

    const header = (
        <Card>
            <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
                <Space direction="vertical" size={8}>
                    {spec.model && (
                        <Space wrap>
                            <Text strong>Model with:</Text>
                            <Radio.Group
                                value={engine}
                                disabled={settingEngine || ingestionStore.runningStep}
                                onChange={(e) => handleEngine(e.target.value)}
                                options={MODEL_ENGINES.map((e) => ({ value: e.value, label: e.label }))}
                                optionType="button"
                            />
                        </Space>
                    )}
                    {headerText && <Text type="secondary">{headerText}</Text>}
                </Space>
                <Space>
                    {spec.model && (
                        <Tooltip title={editableModelCount === 0 ? 'No variant has a .glb model yet' : undefined}>
                            <Button icon={<EditOutlined />} disabled={editableModelCount === 0} onClick={() => setModelEditorOpen(true)}>
                                Model editor
                            </Button>
                        </Tooltip>
                    )}
                    <Button type="primary" loading={ingestionStore.runningStep} onClick={handleRunStep}>
                        {spec.model
                            ? allReady
                                ? 'Approve & continue'
                                : textureRemainingState
                                  ? 'Texture remaining variants'
                                  : 'Generate model'
                            : spec.texture
                              ? allReady ? 'Run assets step' : 'Generate texture'
                              : 'Run assets step'}
                    </Button>
                </Space>
            </Space>
            {spec.needed && !allReady && (
                <Alert
                    style={{ marginTop: 12 }}
                    type="warning"
                    showIcon
                    message="Some variants are missing their asset"
                    description={
                        spec.texture
                            ? 'Run the step to auto-generate a texture for each variant, or upload your own below.'
                            : textureRemainingState
                              ? 'Click "Texture rest" on a model (or "Texture remaining variants" for all of them) to texture the variants that share it, or upload a model manually below.'
                              : `Generate, build or upload a ${spec.required} for each model below, then run the step.`
                    }
                />
            )}
        </Card>
    );

    const modelSections = spec.model && groupList.map((group) => {
        const rep = representativeOf(group);
        return (
            <ModelSection
                key={rep.id}
                item={item}
                title={sectionTitleOf(item, rep, groupList.length, geometryGroupLabel(item, rep))}
                group={group}
                representative={rep}
                byVariant={byVariant}
                opus={opus}
                isBed={isBed}
                hasModel={hasModel}
                labelOf={labelOf}
                onRebuild={handleRegenerate}
                onRegenerate={handleRegenerate}
                onRetexture={handleRetexture}
                onModelConfigChange={(variantId, cfg) => setConfigByVariant((prev) => ({ ...prev, [variantId]: cfg }))}
                onTextureRest={handleTextureRest}
                onUpload={handleUpload}
                onDownload={handleDownload}
                busy={busyFor(rep.id)}
                texturing={texturingRest === rep.id || retexturing === rep.id}
            />
        );
    });

    const variantCards = spec.needed && sortedVariants(item).map((variant) => {
        const row = byVariant[variant.id];
        const assets = (row?.assets || []).filter((a) => a.kind === 'original');
        const isRep = representatives.has(variant.id);
        const rep = spec.model ? representativeOf(groupList.find((g) => g.includes(variant))) : null;
        const assetsTab = (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {variant.meshyConfig?.lastError && (!spec.model || !isRep) && (
                        <Alert type="error" showIcon message="Asset generation failed" description={variant.meshyConfig.lastError} />
                    )}
                    {spec.texture ? (
                        <TextureAssetPanel
                            itemId={item.id}
                            variantId={variant.id}
                            assets={assets}
                            onUpload={(file) => handleUpload(variant.id, file)}
                            onReplace={(url) => handleUseAsAsset(variant.id, { url })}
                            onGenerateFrom={(source) => handleGenerateTextureFrom(variant.id, source)}
                            onDownload={(a, name) => handleDownload(a, name)}
                        />
                    ) : (
                        <VariantAssetViewer
                            itemId={item.id}
                            variantId={variant.id}
                            assets={assets}
                            itemType={item.itemType}
                            modelsOnly
                            onDownload={(a, name) => handleDownload(a, name)}
                        />
                    )}
                    {spec.model && !isRep && rep && hasModel(rep) && (
                        <Tooltip title={`Textures this variant again from the ${labelOf(rep)} model, styled by its own image`}>
                            <Button
                                size="small"
                                icon={<BgColorsOutlined />}
                                loading={retexturing === variant.id}
                                disabled={busyFor(variant.id)}
                                onClick={() => handleRetexture(variant.id, { sourceVariantId: rep.id })}
                            >
                                {row?.hasRequired ? 'Retexture again' : 'Texture from the model'}
                            </Button>
                        </Tooltip>
                    )}
                </Space>
        );
        return (
            <Card
                key={variant.id}
                size="small"
                title={labelOf(variant)}
                extra={spec.model && isRep && groupList.length < (item.variants || []).length && <Tag>group model</Tag>}
            >
                <Tabs
                    className="assets-step-tabs"
                    items={[
                        { key: 'assets', icon: <CodepenOutlined />, label: spec.model ? '3D' : 'Assets', children: assetsTab },
                        { key: 'images', icon: <PictureOutlined />, label: 'Product images', children: <VariantImages itemId={item.id} variant={variant} refreshTick={refreshTick} /> },
                    ]}
                />
            </Card>
        );
    });

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {item.itemType === 'Rug' && (
                <Card size="small">
                    <Space>
                        <Text strong>Rug class:</Text>
                        <Radio.Group
                            value={item.rugClass || 'flat'}
                            disabled={settingRugClass}
                            onChange={(e) => handleRugClass(e.target.value)}
                            options={[
                                { value: 'flat', label: 'Flat plane (texture)' },
                                { value: 'complex', label: 'Complex 3D shape (model)' },
                            ]}
                            optionType="button"
                        />
                    </Space>
                </Card>
            )}
            {header}
            {modelSections}
            {variantCards}
            {/* Mounted (closed) as soon as the step renders: the Babylon engine boots
                and every variant's latest original .glb preloads in the background,
                so the editor opens instantly. */}
            {spec.model && (
                <ModelEditor open={modelEditorOpen} item={item} assetsByVariant={byVariant} onClose={() => setModelEditorOpen(false)} onSaved={loadAssets} />
            )}
        </Space>
    );
});
